import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  Users, 
  Calendar,
  UserPlus,
  Settings,
  User,
  X,
  Shield,
  Globe,
  Briefcase,
  DollarSign,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../Auth/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { hasPermission } from '../../utils/permissions';
import apiService from '../../services/api';

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const [pendingAgentCount, setPendingAgentCount] = useState(0);
  const [activeSection, setActiveSection] = useState(null);

  // Fetch pending agent applications count (for badge); refetch when notifications or route change
  useEffect(() => {
    if (!user || !hasPermission(user, 'agent_applications.view')) return;
    apiService.getAgentApplicationsCount('pending').then(setPendingAgentCount).catch(() => setPendingAgentCount(0));
  }, [user, unreadCount, location.pathname]);

  const customerNavigation = [
    { name: 'Dashboard', href: '/app', icon: Home, permission: null, children: [] },
    { name: 'Services', href: '/app/services', icon: Settings, permission: null, children: [] },
    { name: 'My Applications', href: '/app/applications', icon: FileText, permission: null, children: [] },
    { name: 'Profile', href: '/app/profile', icon: User, permission: null, children: [] },
  ];

  const adminNavigation = [
    {
      name: 'Dashboard',
      href: '/app',
      icon: Home,
      permission: null,
      children: [
        { name: 'Overview', href: '/app' },
        { name: 'Reports', href: '/app/reports', permission: 'reports.view' },
        { name: 'Data Export', href: '/app/reports', permission: 'reports.view' },
        { name: 'Activity Log', href: '/app/reports', permission: 'reports.view' },
      ],
    },
    {
      name: 'Applications',
      href: '/app/applications',
      icon: FileText,
      permission: 'applications.view',
      children: [],
    },
    {
      name: 'Customers',
      href: '/app/customers',
      icon: Users,
      permission: 'customers.view',
      children: [],
    },
    {
      name: 'Appointments & Visits',
      href: '/app/appointments',
      icon: Calendar,
      permissionsAny: ['appointments.view', 'visits.view', 'visitors.view'],
      children: [
        { name: 'Visitors', href: '/app/visitors', permission: 'visitors.view' },
        { name: 'Visits Log', href: '/app/visits', permission: 'visits.view' },
        { name: 'Appointments Calendar', href: '/app/appointments', permission: 'appointments.view' },
        { name: 'Schedule New Appointment', href: '/app/appointments', permission: 'appointments.view' },
        { name: 'Check-in / Check-out', href: '/app/visitors', permission: 'visitors.view' },
      ],
    },
    {
      name: 'Services',
      href: '/app/services',
      icon: Settings,
      permissionsAny: ['services.view', 'service_categories.view'],
      children: [
        { name: 'All Services', href: '/app/services', permission: 'services.view' },
        { name: 'Service Categories', href: '/app/service-categories', permission: 'service_categories.view' },
      ],
    },
    {
      name: 'Revenue',
      href: '/app/reports',
      icon: DollarSign,
      permission: 'reports.view',
      children: [
        { name: 'Income Overview', href: '/app/reports?report=overview', permission: 'reports.view' },
        { name: 'Invoices', href: '/app/reports?report=applications', permission: 'reports.view' },
        { name: 'Payments Received', href: '/app/reports?report=revenue', permission: 'reports.view' },
        { name: 'Payment Methods', href: '/app/reports?report=revenue', permission: 'reports.view' },
        { name: 'Financial Reports', href: '/app/reports?report=customers', permission: 'reports.view' },
      ],
    },
    {
      name: 'Website',
      href: '/app/website',
      icon: Globe,
      permission: 'website.view',
      children: [
        { name: 'Pages', href: '/app/website?tab=pages', permission: 'website.view' },
        { name: 'Menus', href: '/app/website?tab=builder', permission: 'website.view' },
        { name: 'Banners / Sliders', href: '/app/website?tab=slider', permission: 'website.view' },
        { name: 'Blog / News', href: '/app/website?tab=blog', permission: 'website.view' },
        { name: 'SEO Settings', href: '/app/website?tab=pages', permission: 'website.view' },
      ],
    },
    {
      name: 'Documents',
      href: '/app/documents',
      icon: FileText,
      permission: 'documents.view',
      children: [],
    },
    {
      name: 'Job Applications',
      href: '/app/job-applications',
      icon: Briefcase,
      permission: 'job_applications.view',
      children: [],
    },
    {
      name: 'Agent Management',
      href: '/app/agent-applications',
      icon: UserPlus,
      permissionsAny: ['agent_applications.view', 'agents.view'],
      badgeKey: 'agent-applications',
      children: [
        { name: 'All Agents', href: '/app/agents', permission: 'agents.view' },
        { name: 'Agent Requests', href: '/app/agent-applications', permission: 'agent_applications.view' },
        { name: 'Agent Performance', href: '/app/agents', permission: 'agents.view' },
        { name: 'Commission Tracking', href: '/app/agents', permission: 'agents.view' },
        { name: 'Assign Territory', href: '/app/agents', permission: 'agents.view' },
      ],
    },
    {
      name: 'User Management',
      href: '/app/users',
      icon: Shield,
      permissionsAny: ['users.view', 'staff.view'],
      children: [
        { name: 'Staff', href: '/app/staff', permission: 'staff.view' },
        { name: 'Users', href: '/app/users', permission: 'users.view' },
        { name: 'Roles & Permissions', href: '/app/users', permission: 'users.view' },
        { name: 'Invite New User', href: '/app/users', permission: 'users.view' },
        { name: 'Login History', href: '/app/users', permission: 'users.view' },
      ],
    },
    {
      name: 'Account Settings',
      href: '/app/profile',
      icon: User,
      permission: null,
      children: [
        { name: 'My Profile', href: '/app/profile' },
        { name: 'Change Password', href: '/app/profile' },
        { name: 'System Settings', href: '/app/settings', permission: 'settings.view' },
        { name: 'Notifications', href: '/app/profile' },
        { name: 'API Keys', href: '/app/settings', permission: 'settings.view' },
      ],
    },
  ];

  const canSeeItem = (item) => {
    if (!item.permission && !item.permissionsAny) return true;
    if (user?.role === 'admin') return true;

    if (user?.role === 'agent') {
      const agentAllowedMenus = ['Applications', 'Customers', 'Services', 'Agent Management', 'Account Settings'];
      if (agentAllowedMenus.includes(item.name)) return true;
    }

    if (Array.isArray(item.permissionsAny) && item.permissionsAny.length > 0) {
      return item.permissionsAny.some((perm) => hasPermission(user, perm));
    }
    return hasPermission(user, item.permission);
  };

  const canSeeChild = (child) => {
    if (!child.permission) return true;
    if (user?.role === 'admin') return true;
    return hasPermission(user, child.permission);
  };

  const navigation = (user?.role === 'customer' ? customerNavigation : adminNavigation)
    .filter(canSeeItem)
    .map((item) => ({
      ...item,
      children: (item.children || []).filter(canSeeChild),
    }));

  const isPathActive = (href) => {
    if (!href) return false;
    if (href.includes('?')) {
      return `${location.pathname}${location.search}` === href;
    }
    // Dashboard should only be active on exact /app
    if (href === '/app') return location.pathname === '/app';
    // Other menus: exact match or nested route
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  useEffect(() => {
    const matched = navigation.find((item) => {
      const isParentActive = isPathActive(item.href);
      const isChildActive = (item.children || []).some(
        (child) => child.href && isPathActive(child.href)
      );
      return isParentActive || isChildActive;
    });
    if (matched) {
      setActiveSection(matched.name);
    }
  }, [location.pathname]); // only route change should auto-open a section

  const toggleSection = (name) => {
    setActiveSection((prev) => (prev === name ? null : name));
  };

  return (
    <>
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 dark:bg-gray-900 opacity-75 dark:opacity-80"></div>
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        flex flex-col h-screen overflow-hidden
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-bold text-primary-600 dark:text-primary-400">VisaLink Africa</h1>
          <button
            className="lg:hidden text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 flex-1 overflow-y-auto sidebar-scrollbar">
          <div className="px-4 pb-6 space-y-2">
            {navigation.map((item) => {
              const isActive = isPathActive(item.href);
              return (
                <div key={item.name} className="space-y-1">
                  <div
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200
                      ${isActive 
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 border-r-2 border-primary-600 dark:border-primary-400' 
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                      }
                    `}
                  >
                    <Link
                      to={item.href}
                      className="flex items-center min-w-0 flex-1"
                      onClick={() => {
                        if (item.children && item.children.length > 0) {
                          toggleSection(item.name);
                        }
                        setSidebarOpen(false);
                      }}
                    >
                      <item.icon className={`
                        mr-3 h-5 w-5 flex-shrink-0
                        ${isActive ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-300'}
                      `} />
                      <span className="truncate">{item.name}</span>
                    </Link>
                    {item.badgeKey === 'agent-applications' && pendingAgentCount > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-semibold rounded-full bg-primary-600 text-white dark:bg-primary-500">
                        {pendingAgentCount > 99 ? '99+' : pendingAgentCount}
                      </span>
                    )}
                    {item.children && item.children.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleSection(item.name)}
                        className="ml-1 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10"
                        aria-label={activeSection === item.name ? `Collapse ${item.name}` : `Expand ${item.name}`}
                      >
                        {activeSection === item.name ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    )}
                  </div>
                  {item.children && item.children.length > 0 && activeSection === item.name && (
                    <div className="ml-8 space-y-1">
                      {item.children.map((child) => {
                        if (child.href) {
                          const childActive = isPathActive(child.href);
                          return (
                            <Link
                              key={`${item.name}-${child.name}`}
                              to={child.href}
                              className={`block px-2 py-1 text-xs rounded transition-colors ${
                                childActive
                                  ? 'text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              {child.name}
                            </Link>
                          );
                        }
                        return (
                          <div
                            key={`${item.name}-${child.name}`}
                            className="px-2 py-1 text-xs text-gray-400 dark:text-gray-500"
                          >
                            {child.name}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
